import {ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {type RouteMeta, type RouteRecordRaw, useRouter} from 'vue-router';
import {useSessionStore} from '@/stores/session-store';
import {useProjects} from '@/queries/projects';
import {useProducts, useSubscribedProducts} from '@/queries/products';
import type {MenuItem, NavGroup} from '@/types/routing-types';
import type {Project} from '@/types';

type ProductLabel = { id: string; name: string };

export const CUSTOM_PRODUCT_LABEL = 'Custom';
export const CUSTOM_PRODUCT_KEY = '__custom__';

function projectKey(p: Project): string {
  return p.subscriberProductId ?? p.publisherProductId ?? CUSTOM_PRODUCT_KEY;
}

function makeProjectLeaf(project: Project): MenuItem {
  return {
    path: `/projects/${project.id}`,
    name: `project-${project.id}`,
    title: project.name || `Project ${project.id}`,
    icon: 'bi bi-folder',
    group: 'main' as NavGroup,
  };
}

export function buildProjectMenuItems(
  projectList: Project[],
  productList: ProductLabel[],
): MenuItem[] {
  if (projectList.length === 0) return [];

  const groups = new Map<string, Project[]>();
  for (const p of projectList) {
    const key = projectKey(p);
    const bucket = groups.get(key) ?? [];
    bucket.push(p);
    groups.set(key, bucket);
  }

  if (groups.size <= 1) {
    return [...projectList]
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map(makeProjectLeaf);
  }

  const productById = new Map(productList.map((pr) => [pr.id, pr]));
  const groupItems: MenuItem[] = [];

  for (const [key, bucket] of groups) {
    const label =
      key === CUSTOM_PRODUCT_KEY
        ? CUSTOM_PRODUCT_LABEL
        : productById.get(key)?.name || key;

    const sortedBucket = [...bucket].sort((a, b) =>
      (a.name ?? '').localeCompare(b.name ?? ''),
    );

    groupItems.push({
      path: '/projects',
      query: { productId: key },
      name: `project-group-${key}`,
      title: label,
      icon: 'bi bi-collection',
      group: 'main' as NavGroup,
      children: sortedBucket.map(makeProjectLeaf),
    });
  }

  groupItems.sort((a, b) => {
    if (a.title === CUSTOM_PRODUCT_LABEL) return 1;
    if (b.title === CUSTOM_PRODUCT_LABEL) return -1;
    return a.title.localeCompare(b.title);
  });

  return groupItems;
}

/**
 * Composable for building and managing navigation menu from routes
 * Uses router as the single source of truth for navigation structure
 */
export function useNavigation() {
  const router = useRouter();
  const sessionStore = useSessionStore();

  /**
   * Helper: Extract typed metadata from route
   */
  function getRouteMetadata(route: any): RouteMeta | null {
    // Check meta first (preferred)
    if (route.meta && typeof route.meta === 'object' && 'sideNav' in route.meta) {
      return route.meta as RouteMeta;
    }
    // Fallback to props for backwards compatibility
    if (route.props && typeof route.props === 'object' && 'sideNav' in route.props) {
      return route.props as RouteMeta;
    }
    return null;
  }

  /**
   * Build menu tree from routes
   * Filters routes with sideNav: true and converts to MenuItem structure
   */
  function buildMenuTree(routes: Readonly<RouteRecordRaw[]>): MenuItem[] {
    const menuItems: MenuItem[] = [];

    for (const route of routes) {
      const metadata = getRouteMetadata(route);

      // Skip routes that aren't in the side navigation
      if (!metadata?.sideNav) continue;

      // Skip routes with a parentRoute (they'll be handled as children)
      if (metadata.parentRoute) continue;

      const menuItem: MenuItem = {
        path: route.path,
        name: route.name,
        title: metadata.title || String(route.name || ''),
        icon: metadata.icon || '',
        group: metadata.group,
        dynamicChildren: metadata.dynamicChildren,
        exact: metadata.exact,
        children: [],
      };

      // Find static children from routes (routes with matching parentRoute)
      if (route.name) {
        const staticChildren = routes
          .filter((r: any) => {
            const childMeta = getRouteMetadata(r);
            return childMeta?.sideNav && childMeta.parentRoute === route.name;
          })
          .map((child: any) => {
            const childMeta = getRouteMetadata(child);
            return {
              path: child.path,
              name: child.name,
              title: childMeta?.title || String(child.name || ''),
              icon: childMeta?.icon || '',
              group: childMeta?.group,
              parentRoute: childMeta?.parentRoute,
            };
          });

        if (staticChildren.length > 0) {
          menuItem.children = staticChildren;
        }
      }

      // Also check for nested children in route definition
      if (route.children && route.children.length > 0) {
        const routeChildren = route.children
          .filter((child: any) => getRouteMetadata(child)?.sideNav)
          .map((child: any) => {
            const childMeta = getRouteMetadata(child);
            return {
              path: child.path.startsWith('/') ? child.path : `${route.path}/${child.path}`,
              name: child.name,
              title: childMeta?.title || String(child.name || ''),
              icon: childMeta?.icon || '',
              group: childMeta?.group,
            };
          });

        // Merge with static children if any
        if (routeChildren.length > 0) {
          menuItem.children = [...(menuItem.children || []), ...routeChildren];
        }
      }

      menuItems.push(menuItem);
    }

    return menuItems;
  }

  /**
   * Load dynamic children for a menu item
   * Supports 'projects' and 'products' dynamic types
   */
  async function loadDynamicChildren(item: MenuItem): Promise<MenuItem[]> {
    if (!item.dynamicChildren) return [];

    if (item.dynamicChildren === 'projects') {
      // Subscriber orgs won't have any of their subscribed products in `products` (that endpoint
      // returns the org's *published* products) — merge both so the group label resolves to a name
      // rather than the bare product GUID.
      const labels: ProductLabel[] = [...products.value, ...subscribedProducts.value];
      return buildProjectMenuItems(projects.value, labels);
    }

    if (item.dynamicChildren === 'products') {
      return products.value.map((product) => ({
        path: `/publisher/${product.id}`,
        name: `publisher-product-${product.id}`,
        title: product.name || `Product ${product.id}`,
        icon: 'bi bi-box',
        group: 'publisher' as NavGroup,
      }));
    }

    return [];
  }

  // storeToRefs gives reactive refs to the session getters so the watch below tracks them.
  // Plain `sessionStore.x` destructuring would capture values at call-time and miss org switches.
  const { canPublishProducts, canPartnerWithOrgs, isSuperAdmin, role } = storeToRefs(sessionStore);

  /**
   * Filter menu items by user capabilities.
   * - publisher/partners: gated by the *current* org's capability flags
   * - admin/dev: shown when the user is a SuperAdmin AND is in an org where they hold a real
   *   Membership (any role). SwitchOrg returns the real role when a Membership exists and the
   *   synthetic `SuperAdminView` role otherwise, so this hides admin/dev exactly when a
   *   SuperAdmin has switched into a foreign org via the SuperAdmin shortcut.
   */
  function filterByCapabilities(items: MenuItem[]): MenuItem[] {
    const isInternalAdmin = isSuperAdmin.value && role.value !== 'SuperAdminView';

    return items.filter(item => {
      if (item.group === 'publisher' && !canPublishProducts.value) return false;
      if (item.group === 'partners' && !canPartnerWithOrgs.value) return false;
      if (item.group === 'admin' && !isInternalAdmin) return false;
      if (item.group === 'dev' && !isInternalAdmin) return false;

      if (item.children && item.children.length > 0) {
        item.children = filterByCapabilities(item.children);
      }

      return true;
    });
  }

  /**
   * Main menu items - reactive ref instead of computed
   * This allows us to mutate children for dynamic loading
   */
  const menuItems = ref<MenuItem[]>([]);

  /**
   * Initialize menu items from routes
   */
  function initializeMenuItems() {
    const tree = buildMenuTree(router.options.routes);
    menuItems.value = filterByCapabilities(tree);
  }

  /**
   * Load all dynamic children for menu items
   */
  async function loadAllDynamicChildren() {
    for (const item of menuItems.value) {
      if (item.dynamicChildren) {
        item.children = await loadDynamicChildren(item);
      }
    }
  }

  // Rebuild and re-filter the menu whenever session-relevant state changes — most importantly
  // on org-switch, where capabilities and role flip. `immediate: true` covers the initial build.
  watch(
    [canPublishProducts, canPartnerWithOrgs, isSuperAdmin, role],
    () => initializeMenuItems(),
    { immediate: true }
  );

  // TanStack Query reactive refs — deduped with any other component calling useProjects/useProducts
  const { projects } = useProjects();
  const { products } = useProducts();
  const { subscribedProducts } = useSubscribedProducts();

  watch(
    () => [projects.value, products.value, subscribedProducts.value],
    async () => {
      await loadAllDynamicChildren();
    },
    { deep: true }
  );

  return {
    menuItems,
    loadDynamicChildren,
    loadAllDynamicChildren,
  };
}
